import { DateTime, Interval } from "luxon";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { ButtonImageControl } from "@/Common/Components/Buttons/ButtonImageControl";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { CallCenterCallReason } from "@/Common/Models/CallCenter/CallCenterCallReason";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { SendCommunicationStrategy } from "@/Communications/Services/Portal/Strategies/SendCommunicationStrategy";
import { PatientCommunicationsItem } from "@/Patient/Models/History/PatientCommunicationsItem";
import { PatientCommunicationsListItem } from "@/Patient/Models/History/PatientCommunicationsListItem";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { CommunicationsPopup, IPatientHistoryCommunicationsProps } from "./CommunicationsPopup";

import img_icon_pencil from "@/Patient/Assets/pencil-icon.png";

function replaceWhiteSpaceString(value: string, replacement: string): string {
    if (IsNullOrWhiteSpace(value)) {
        return replacement;
    }
    return value;
}

class CommunicationsView extends React.Component<IPatientHistoryCommunicationsProps, any> {
    private readonly isPortalEnabled: boolean = cacheStore.ClinicSettings_Portal.EnablePortalWebsite &&
        !cacheStore.ClinicSettings.EnableBidirectionalCommunication;
    private readonly isPortalInvitationFlowV2Enabled: boolean = cacheStore.ClinicSettings.EnablePortalInvitationFlowV2;
    public render(): JSX.Element {
        const communicationsList = this.props.Model.get("CommunicationsList").sort(
            (communicationItem, communicationItemToBeCompared) => {
                if (communicationItem.Model.CreationDate < communicationItemToBeCompared.Model.CreationDate)
                    return 1;
                else if (communicationItem.Model.CreationDate > communicationItemToBeCompared.Model.CreationDate)
                    return -1;
                else
                    return 0;
            }
        );

        let communicationRender = communicationsList.map((communication: PatientCommunicationsListItem) => {
            let portalSyncButton: JSX.Element = null;

            if (this.isPortalEnabled && (this.props.PatientModel?.IsRegisteredToPortal() || this.isPortalInvitationFlowV2Enabled)) {
                const buttonProps = PortalHelper.getPortalButtonPropsForShareableItem(
                    communication.Model
                );

                portalSyncButton = (
                    <PortalStatusButton
                        className="push-left-5"
                        icon="share"
                        onClick={() => this.portalStatusClicked(communication)}
                        {...buttonProps}
                    />
                );
            }

            let followupComponent: JSX.Element = (<p style={{ wordBreak: "break-word" }}><strong>{communication.Model.FollowUpString}</strong></p>);
            let editRecordButton: JSX.Element = null;

            if (this.isCallCenterCommunication(communication.Model)) {
                const translatedCallReasons = this.getTranslatedCallReasonValues(communication.Model.CallReasons);
                const callDateAndTime = " | " + this.getCallDateAndTimeValues(communication.Model.CallStart);
                let duration = "";
                if (communication.Model.CallEnd) {
                    duration = " | " + this.getDurationValue(communication.Model.CallStart, communication.Model.CallEnd);
                }

                if (this.isLessThan24HoursOld(communication.Model.CreationDate)) {
                    editRecordButton = (
                        <ButtonImageControl
                            style={{ verticalAlign: "bottom" }}
                            onClick={() => this.editCorrespondenceRecordClicked(communication)}
                            imagePath={img_icon_pencil}
                        />
                    );
                }

                followupComponent = (
                    <p style={{ wordBreak: "break-word" }}>
                        <strong>
                            {communication.Model.FollowUpString}
                            {translatedCallReasons}
                            {callDateAndTime}
                            {duration}
                        </strong>
                    </p>
                );
            }

            const creationDate = communication.Model.getFormatedCreationDate();

            return (
                <div key={communication.Model.Id} className="row" style={{ paddingBottom: "10px" }}>
                    <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                        <p><label className="form-label">{communication.Model.FromEmployee}</label></p>
                        <p><label className="form-label">{creationDate}</label></p>
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-6 bottom-border">
                        {followupComponent}
                        <p style={{ wordBreak: "break-word" }}><strong>{replaceWhiteSpaceString(communication.Model.Note, "\u00A0")}</strong></p>
                    </div>
                    <div><span> {portalSyncButton} {editRecordButton} </span></div>
                </div>
            );
        });

        return (
            <div className="patient-communications-container">
                {this.props.Model.get("IsSendToPatientPortalPopupOpen") && (
                    <SendToPortalPopup
                        model={this.props.Model.get("PortalLinkedItemModel")}
                        strategy={new SendCommunicationStrategy()}
                    />
                )}
                {this.props.Model.get("ShowNewCommunicationPopup") && (
                    <CommunicationsPopup
                        PatientModel={this.props.PatientModel}
                        Model={this.props.Model}
                    />
                )}

                <div className="banner">
                    <h3 className="thin-font no-margin" title="Banner Message">{cacheStore.GetString("CorrespondenceLabel")}</h3>
                </div>

                <div data-hvar="60" className="scrollable no-x-overflow patient-communications-sub-container">
                    {communicationRender}
                </div>

                <div className="meta-footer">
                    <ButtonControl
                        onClick={() => sessionStore.dispatch(LocalPatientOperations.History.Communications_AddNewCommunication)}
                        className="btn btn-primary pull-right"
                        label={cacheStore.GetString("NewEntryLabel")}
                    />
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        sessionStore.dispatchAsync(LocalPatientOperations.History.Communications_LoadPatientCommunication);

        $(window).trigger("resize");
    }

    private isCallCenterCommunication(item: PatientCommunicationsItem): boolean {
        if (!cacheStore.ClinicSettings.IsCallCenterEnabled()) {
            return false;
        }

        if (!item.ProviderId || !item.CallId) {
            return false;
        }

        const followUpCode = cacheStore.GetCodeByLookupId(item.FollowUpId);
        return (followUpCode == "IncomingCall" || followUpCode == "OutgoingCall");
    }

    private isLessThan24HoursOld(dateAddedMoment: DateTime): boolean {
        const now = DateTime.local();
        if (dateAddedMoment > now) {
            return true; // item was created in the future of client's time
        }
        const interval = Interval.fromDateTimes(dateAddedMoment, now);
        return interval.length("hours") < 24;
    }

    private editCorrespondenceRecordClicked(item: PatientCommunicationsListItem): void {
        let operation = new LocalEvent(LocalPatientOperations.History.Communications_EditCallCommunications);
        operation.AddItem("item", item);
        LocalEventDispatcher.dispatch(operation);
    }

    private getTranslatedCallReasonValues(values: CallCenterCallReason[]): string {
        if (values.length == 0) {
            return "";
        }
        return " - " + values
            .map(value => CallCenterCallReason[value])
            .map(enumName =>
                cacheStore.FindLookupByLookupCode(enumName, "CallCenter_CallReason")?.GetValue() ?? enumName
            )
            .join(", ");
    }

    private getCallDateAndTimeValues(start: string | DateTime): string {
        const startDate = LuxonDateTimeParser.parse(start).toLocal();

        const formattedDate = startDate.toFormat("yyyy/MM/dd");
        const formattedStartTime = startDate.toFormat("HH:mm");

        const onLabel = cacheStore.GetString("OnLabel");
        const atLabel = cacheStore.GetString("AtLabel");
        return `${onLabel}${formattedDate} ${atLabel} ${formattedStartTime}`;
    }

    private getDurationValue(start: string | DateTime, end: string | DateTime): string {
        const startDate = LuxonDateTimeParser.parse(start).toLocal();
        const endDate = LuxonDateTimeParser.parse(end).toLocal();
        const duration = Interval.fromDateTimes(startDate, endDate).toDuration();

        const durationLabel = cacheStore.GetString("DurationLabel");

        const seconds = Math.ceil(duration.as("seconds"));

        if (seconds < 60) {
            return `${durationLabel}: ${seconds} ${cacheStore.GetString("CallCenter_Correspondence_Seconds")}`;
        }

        const minutes = Math.floor(duration.as("minutes"));

        if (minutes < 2) {
            return `${durationLabel}: 1 ${cacheStore.GetString("CallCenter_Correspondence_Minute")}`;
        }
        return `${durationLabel}: ${Math.round(minutes)} ${cacheStore.GetString("MinuteLLabel")}`;
    }

    private portalStatusClicked(item: PatientCommunicationsListItem): void {
        let operation = new LocalEvent(LocalPatientOperations.History.Communications_onPortalStatusClicked);
        operation.AddItem("item", item);
        LocalEventDispatcher.dispatch(operation);
    }
}

export { CommunicationsView };
